<template>
  <div class="upload-container">
    <el-card class="upload-card">
      <template #header>
        <div class="card-header">
          <span>{{ isEditMode ? '编辑报告' : '上传报告文件' }}</span>
        </div>
      </template>
      
      <el-form :model="form" :rules="rules" ref="uploadForm" :label-width="isMobile ? '100px' : '120px'" class="responsive-upload-form">
        <el-form-item label="合作公司" prop="partnerCompany">
          <el-select v-model="form.partnerCompany" placeholder="请选择合作公司" style="width: 100%">
            <el-option label="宝通" value="宝通"></el-option>
            <el-option label="必维" value="必维"></el-option>
            <el-option label="柏纶" value="柏纶"></el-option>
            <el-option label="德凯" value="德凯"></el-option>
            <el-option label="中鼎" value="中鼎"></el-option>
          </el-select>
        </el-form-item>
        
        <el-form-item label="产品名称" prop="productName">
          <el-input v-model="form.productName" placeholder="请输入产品名称"></el-input>
        </el-form-item>
        
        <el-form-item label="产品型号" prop="productModel">
          <el-input v-model="form.productModel" placeholder="请输入产品型号"></el-input>
        </el-form-item>
        
        <el-form-item label="产品描述">
          <el-input 
            v-model="form.productDescription" 
            type="textarea" 
            :rows="3"
            placeholder="请输入产品描述（可选）">
          </el-input>
        </el-form-item>
        
        <!-- 编辑模式下显示原有附件 -->
        <el-form-item v-if="isEditMode && originalReport && originalReport.attachment" label="当前附件">
          <div class="current-attachment">
            <div class="attachment-info">
              <el-icon class="attachment-icon"><Document /></el-icon>
              <div class="attachment-details">
                <div class="attachment-name">{{ originalReport.attachment.fileName }}</div>
                <div class="attachment-meta">
                  <span class="attachment-size">{{ formatFileSize(originalReport.attachment.size) }}</span>
                  <span class="attachment-type">{{ originalReport.attachment.type }}</span>
                </div>
              </div>
            </div>
            <div class="attachment-actions">
              <el-button size="small" type="primary" @click="viewCurrentAttachment">查看</el-button>
              <el-button size="small" type="success" @click="downloadCurrentAttachment">下载</el-button>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item label="报告文件" prop="reportFile">
          <el-upload
            ref="upload"
            class="upload-demo"
            :action="uploadUrl"
            :data="uploadData"
            :on-success="handleSuccess"
            :on-error="handleError"
            :on-progress="handleProgress"
            :before-upload="beforeUpload"
            :auto-upload="false"
            :limit="1"
            name="reportFile"
            accept=".pdf,.doc,.docx,.txt,.xlsx,.xls,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.rar,.7z">
            <template #trigger>
              <el-button type="primary">选择文件</el-button>
            </template>
            <template #tip>
              <div class="el-upload__tip">
                支持多种文件格式：文档(PDF、DOC、DOCX、TXT、XLSX、XLS、PPT、PPTX)、图片(JPG、PNG、GIF)、压缩包(ZIP、RAR、7Z)，文件大小不超过 50MB
              </div>
              <!-- 上传进度条 -->
              <div v-if="uploading" class="upload-progress">
                <el-progress 
                  :percentage="uploadProgress" 
                  :stroke-width="6"
                  :show-text="true"
                  :format="formatProgress"
                ></el-progress>
              </div>
            </template>
          </el-upload>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="submitUpload" :loading="uploading">
            {{ uploading ? '上传中...' : '上传文件' }}
          </el-button>
          <el-button @click="resetForm">重置</el-button>
          <el-button type="info" @click="viewReports">查看所有报告</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 上传成功后显示二维码 -->
    <el-card v-if="uploadResult" class="result-card">
      <template #header>
        <div class="card-header">
          <span>上传成功</span>
        </div>
      </template>
      
      <div class="result-content">
        <div class="qr-code-section">
          <h3>扫描二维码查看报告</h3>
          <img :src="uploadResult.qrCodeUrl" alt="二维码" class="qr-code">
          <p>报告ID: {{ uploadResult.reportId }}</p>
        </div>
        
        <div class="links-section">
          <el-button type="primary" @click="viewReport">直接查看报告</el-button>
          <el-button type="success" @click="downloadQR">下载二维码</el-button>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios'
import { Document } from '@element-plus/icons-vue'

export default {
  name: 'Upload',
  components: {
    Document
  },
  props: {
    id: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      form: {
        partnerCompany: '',
        productName: '',
        productModel: '',
        productDescription: ''
      },
      rules: {
        partnerCompany: [
          { required: true, message: '请选择合作公司', trigger: 'change' }
        ],
        productName: [
          { required: true, message: '请输入产品名称', trigger: 'blur' }
        ],
        productModel: [
          { required: true, message: '请输入产品型号', trigger: 'blur' }
        ]
      },
      uploading: false,
      uploadResult: null,
      uploadUrl: '/api/upload-report',
      isEditMode: false,
      originalReport: null,
      loading: false,
      uploadProgress: 0
    }
  },
  computed: {
    uploadData() {
      return {
        partnerCompany: this.form.partnerCompany,
        sampleName: this.form.productName,
        sampleNumber: this.form.productModel,
        productDescription: this.form.productDescription
      }
    }
  },
  async mounted() {
    if (this.id) {
      this.isEditMode = true
      await this.loadReportForEdit()
    }
  },
  methods: {
    beforeUpload(file) {
      // 扩展支持的文件类型
      const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.xlsx', '.xls', 
        '.ppt', '.pptx', '.jpg', '.jpeg', '.png', '.gif', '.zip', '.rar', '.7z'];
      const fileName = file.name.toLowerCase();
      const isValidType = allowedExtensions.some(ext => fileName.endsWith(ext));
      const isLt50M = file.size / 1024 / 1024 < 50;

      if (!isValidType) {
        this.$message.error('支持的文件格式: PDF、DOC、DOCX、TXT、XLSX、XLS、PPT、PPTX、JPG、PNG、GIF、ZIP、RAR、7Z');
        return false;
      }
      if (!isLt50M) {
        this.$message.error('文件大小不能超过 50MB!');
        return false;
      }
      
      console.log('文件验证通过:', file.name, '大小:', (file.size / 1024 / 1024).toFixed(2) + 'MB');
      return true;
    },
    
    submitUpload() {
      this.$refs.uploadForm.validate((valid) => {
        if (valid) {
          console.log('开始上传，产品信息:', this.form);
          this.uploading = true;
          this.uploadProgress = 0;
          
          // 如果是编辑模式，修改上传URL
          if (this.isEditMode) {
            this.$refs.upload.action = `/api/update-report/${this.id}`;
          }
          
          this.$refs.upload.submit();
        } else {
          this.$message.error('请填写必要信息');
          return false;
        }
      });
    },
    
    handleSuccess(response, file) {
      console.log('上传响应:', response);
      this.uploading = false;
      if (response.success) {
        this.uploadResult = response;
        this.$message.success('文件上传成功!');
      } else {
        console.error('上传失败响应:', response);
        this.$message.error(response.error || '上传失败');
      }
    },
    
    handleError(error) {
      console.error('上传错误:', error);
      this.uploading = false;
      this.$message.error('上传失败: ' + error.message);
    },
    
    resetForm() {
      this.$refs.uploadForm.resetFields();
      this.$refs.upload.clearFiles();
      this.uploadResult = null;
    },
    
    viewReport() {
      if (this.uploadResult) {
        this.$router.push(`/view/${this.uploadResult.reportId}`);
      }
    },
    
    viewReports() {
      this.$router.push('/reports');
    },
    
    downloadQR() {
      if (this.uploadResult) {
        const link = document.createElement('a');
        link.href = this.uploadResult.qrCodeUrl;
        link.download = `qr-code-${this.uploadResult.reportId}.png`;
        link.click();
      }
    },
    
    // 编辑模式相关方法
    async loadReportForEdit() {
      try {
        this.loading = true
        // 添加时间戳防缓存
        const timestamp = Date.now()
        const response = await axios.get(`/api/report/${this.id}?_t=${timestamp}`)
        
        if (response.data.success) {
          this.originalReport = response.data.data
          // 填充表单数据
          this.form.productName = this.originalReport.sampleName || ''
          this.form.productModel = this.originalReport.sampleNumber || ''
          this.form.productDescription = this.originalReport.productInfo?.description || ''
        } else {
          this.$message.error('加载报告信息失败')
        }
      } catch (error) {
        console.error('加载报告错误:', error)
        this.$message.error('加载报告信息失败')
      } finally {
        this.loading = false
      }
    },
    
    viewCurrentAttachment() {
      if (this.originalReport && this.originalReport.attachment) {
        this.$router.push(`/pdf-viewer/${this.id}`)
      }
    },
    
    downloadCurrentAttachment() {
      if (this.originalReport && this.originalReport.attachment) {
        const link = document.createElement('a')
        link.href = `/api/download-file/${this.id}`
        link.download = this.originalReport.attachment.fileName
        link.click()
      }
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    handleProgress(event, file, fileList) {
      this.uploadProgress = Math.round(event.percent)
    },
    
    formatProgress(percentage) {
      return `${percentage}%`
    }
  }
}
</script>

<style scoped>
.upload-container {
  max-width: 800px;
  margin: 0 auto;
}

.upload-card, .result-card {
  margin-bottom: 20px;
}

.card-header {
  font-size: 18px;
  font-weight: bold;
}

.result-content {
  text-align: center;
}

.qr-code-section {
  margin-bottom: 20px;
}

.qr-code {
  max-width: 200px;
  margin: 10px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.links-section {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.upload-demo {
  width: 100%;
}

/* 当前附件样式 */
.current-attachment {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 16px;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.attachment-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.attachment-icon {
  font-size: 24px;
  color: #409eff;
  margin-right: 12px;
}

.attachment-details {
  flex: 1;
}

.attachment-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  margin-bottom: 4px;
}

.attachment-meta {
  font-size: 12px;
  color: #909399;
}

.attachment-size {
  margin-right: 12px;
}

.attachment-type {
  background-color: #e1f3d8;
  color: #67c23a;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
}

.attachment-actions {
  display: flex;
  gap: 8px;
}

/* 上传进度条样式 */
.upload-progress {
  margin-top: 12px;
  padding: 8px 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .current-attachment {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .attachment-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>